import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { UserPlus, UsersThree, Coins, Warning, Copy, X, Storefront } from "@phosphor-icons/react";

const emptyForm = { name: "", email: "", phone: "", credit_dollars: "", mode: "invite", temp_password: "" };

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">{title}</h3>
          <button data-testid="reseller-modal-close" onClick={onClose} className="rounded-full p-1 hover:bg-white/10"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ResellerPortal() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isError, isLoading } = useQuery({
    queryKey: ["reseller-summary"],
    queryFn: async () => (await api.get("/reseller/summary")).data,
    retry: false,
  });

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [withdraw, setWithdraw] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "bank", detail: "" });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["reseller-summary"] });
    qc.invalidateQueries({ queryKey: ["wallet"] });
  };

  const bulkInvite = async () => {
    const emails = bulkText.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!emails.length) { toast.error("Paste at least one email"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/reseller/members/bulk", { emails });
      toast.success(`${res.created.length} invited · ${res.skipped.length} skipped`);
      setBulkResult(res); setBulkText(""); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const requestPayout = async () => {
    const cents = Math.round(parseFloat(payForm.amount || "0") * 100);
    if (!cents || cents <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await api.post("/reseller/payout", { amount_cents: cents, method: payForm.method, detail: payForm.detail || undefined });
      toast.success("Payout requested");
      setPayForm({ amount: "", method: "bank", detail: "" }); setWithdraw(false); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const downloadStatement = async () => {
    const month = new Date().toISOString().slice(0, 7);
    try {
      const res = await api.get(`/reseller/statement.pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `commission-${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendTestStatement = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/reseller/statement/test");
      toast.success(`Test statement sent to ${res.sent_to}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="reseller-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <Storefront size={40} className="mx-auto text-muted-foreground" weight="duotone" />
        <h1 className="mt-4 font-display text-2xl font-semibold">Reseller access only</h1>
        <p className="mt-2 text-sm text-muted-foreground">This area is for reseller and distributor accounts.</p>
        <button onClick={() => navigate("/app")} className="mt-6 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Back to app</button>
      </div>
    );
  }

  const s = data || {};

  const createMember = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    if (form.mode === "temp_password" && form.temp_password && form.temp_password.length < 8) {
      toast.error("Temporary password must be at least 8 characters"); return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.post("/reseller/members", {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || undefined,
        credit_dollars: parseFloat(form.credit_dollars || "0") || 0,
        mode: form.mode, temp_password: form.mode === "temp_password" ? (form.temp_password || undefined) : undefined,
      });
      toast.success("Member added");
      setCreated({ email: form.email.trim(), ...res });
      setForm(emptyForm); setAdding(false); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="reseller-portal">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Reseller Portal</h1>
          <p className="mt-2 text-muted-foreground">Sign up new members and earn commission on every account.</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button data-testid="reseller-bulk-btn" onClick={() => { setBulkResult(null); setBulk(true); }} disabled={s.enabled === false}
            className="rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-white/5 disabled:opacity-50">Bulk invite</button>
          <button data-testid="reseller-add-member-btn" onClick={() => { setForm(emptyForm); setAdding(true); }} disabled={s.enabled === false}
            className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            <UserPlus size={16} weight="bold" /> Add member
          </button>
        </div>
      </div>

      {!s.eligible && s.requires_deposit && (
        <div data-testid="deposit-notice" className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          <Warning size={20} weight="fill" className="mt-0.5 shrink-0 text-amber-400" />
          <div>
            <p className="font-semibold text-amber-300">Deposit funds to unlock commission &amp; wholesale pricing</p>
            <p className="mt-1 text-muted-foreground">You can add members now, but reseller commission ({money(s.commission_cents || 0)} per member) is credited only once you hold a paid deposit balance.</p>
            <button onClick={() => navigate("/app/wallet")} className="mt-2 rounded-full bg-amber-500 px-4 py-1.5 text-xs font-semibold text-black">Add funds</button>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><UsersThree size={14} /> Members</div>
          <p className="mt-2 font-mono-metric text-2xl font-semibold" data-testid="member-count">{s.member_count || 0}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Coins size={14} /> Commission earned</div>
          <p className="mt-2 font-mono-metric text-2xl font-semibold text-emerald-400" data-testid="commission-earned">{money(s.commission_earned_cents || 0)}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Coins size={14} /> Per member</div>
          <p className="mt-2 font-mono-metric text-2xl font-semibold">{money(s.commission_cents || 0)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Coins size={14} /> Available to withdraw</div>
          <p className="mt-1 font-mono-metric text-2xl font-semibold text-emerald-400" data-testid="commission-available">{money(s.available_cents || 0)}</p>
          {s.withdrawn_cents > 0 && <p className="text-xs text-muted-foreground">Withdrawn to date: {money(s.withdrawn_cents)}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="test-statement-btn" onClick={sendTestStatement} disabled={busy}
            className="rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-white/5 disabled:opacity-50">Send me a test</button>
          <button data-testid="statement-btn" onClick={downloadStatement}
            className="rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-white/5">Download statement</button>
          <button data-testid="withdraw-btn" onClick={() => setWithdraw(true)} disabled={(s.available_cents || 0) <= 0}
            className="rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">Withdraw</button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="grid grid-cols-12 gap-2 border-b border-white/10 px-4 py-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div className="col-span-3">Name</div><div className="col-span-4">Email</div><div className="col-span-2">Status</div><div className="col-span-3 text-right">Commission</div>
        </div>
        {(s.members || []).map((m) => (
          <div key={m.id} data-testid={`member-row-${m.id}`} className="grid grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-3 text-sm last:border-0">
            <div className="col-span-3 truncate">{m.name || "—"}</div>
            <div className="col-span-4 truncate text-muted-foreground">{m.email}</div>
            <div className="col-span-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.invite_pending ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                {m.invite_pending ? "Pending" : "Active"}
              </span>
            </div>
            <div className="col-span-3 text-right font-mono-metric font-semibold text-emerald-400" data-testid={`member-commission-${m.id}`}>
              {m.commission_cents > 0 ? money(m.commission_cents) : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
        ))}
        {(s.members || []).length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground">No members yet — add your first above.</div>}
      </div>

      {withdraw && (
        <Modal title="Withdraw commission" onClose={() => setWithdraw(false)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Available: <span className="font-mono-metric text-emerald-400">{money(s.available_cents || 0)}</span>. Your commission already spends like credit in-app — withdraw to cash out to a bank or PayPal.</p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amount (USD)</label>
              <input data-testid="payout-amount" type="number" min="0" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder="10.00"
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Method</label>
              <div className="mt-1 flex gap-2">
                {[["bank", "Bank"], ["paypal", "PayPal"]].map(([v, l]) => (
                  <button key={v} data-testid={`payout-method-${v}`} onClick={() => setPayForm({ ...payForm, method: v })}
                    className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${payForm.method === v ? "bg-primary text-primary-foreground" : "border border-border"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payout detail ({payForm.method === "paypal" ? "PayPal email" : "account / IBAN"})</label>
              <input data-testid="payout-detail" value={payForm.detail} onChange={(e) => setPayForm({ ...payForm, detail: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <button data-testid="payout-submit" onClick={requestPayout} disabled={busy}
              className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Requesting…" : "Request payout"}</button>
          </div>
        </Modal>
      )}

      {bulk && (
        <Modal title="Bulk invite members" onClose={() => setBulk(false)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Paste emails separated by commas, spaces, or new lines. Each gets an invite to set their own password. Up to 200 at a time.</p>
            <textarea data-testid="reseller-bulk-emails" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6}
              placeholder="alice@example.com, bob@example.com&#10;carol@example.com"
              className="w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-xs outline-none focus:border-primary" />
            <button data-testid="reseller-bulk-submit" onClick={bulkInvite} disabled={busy}
              className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Inviting…" : "Send invites"}</button>
            {bulkResult && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm" data-testid="reseller-bulk-result">
                <p className="text-emerald-400">Invited: {bulkResult.created.length}</p>
                {bulkResult.skipped.length > 0 && (
                  <div className="mt-1 text-muted-foreground">
                    <p>Skipped: {bulkResult.skipped.length}</p>
                    <ul className="mt-1 max-h-24 overflow-y-auto text-xs">
                      {bulkResult.skipped.map((s2, i) => <li key={i}>{s2.email} — {s2.reason}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {adding && (
        <Modal title="Add a member" onClose={() => setAdding(false)}>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Full name</label>
              <input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</label>
              <input data-testid="member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone (optional)</label>
                <input data-testid="member-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+237…"
                  className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Starting credit</label>
                <input data-testid="member-credit" type="number" min="0" step="0.01" value={form.credit_dollars} onChange={(e) => setForm({ ...form, credit_dollars: e.target.value })} placeholder="0.00"
                  className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Onboarding</label>
              <div className="mt-1 flex gap-2">
                {[["invite", "Email invite"], ["temp_password", "Set temp password"]].map(([v, l]) => (
                  <button key={v} data-testid={`member-mode-${v}`} onClick={() => setForm({ ...form, mode: v })}
                    className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${form.mode === v ? "bg-primary text-primary-foreground" : "border border-border"}`}>{l}</button>
                ))}
              </div>
            </div>
            {form.mode === "temp_password" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Temporary password (blank = auto)</label>
                <input data-testid="member-temp-password" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} placeholder="min 8 characters"
                  className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
              </div>
            )}
            <button data-testid="create-member-submit" onClick={createMember} disabled={busy}
              className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Creating…" : "Create member"}</button>
          </div>
        </Modal>
      )}

      {created && (
        <Modal title="Member created" onClose={() => setCreated(null)}>
          <div className="space-y-3 text-sm" data-testid="member-created">
            <p className="text-muted-foreground">Account for <span className="font-medium text-foreground">{created.email}</span> is ready.</p>
            {created.commission_cents != null && (
              <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-emerald-400">Commission credited: {money(created.commission_cents)}</p>
            )}
            {created.temp_password && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Temporary password</p>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 rounded bg-background px-2 py-1 font-mono-metric text-sm">{created.temp_password}</code>
                  <button onClick={() => { navigator.clipboard.writeText(created.temp_password); toast.success("Copied"); }} className="rounded-lg p-1.5 hover:bg-white/10"><Copy size={16} /></button>
                </div>
              </div>
            )}
            {created.invite_link && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Invite link (also emailed)</p>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-background px-2 py-1 text-xs">{created.invite_link}</code>
                  <button onClick={() => { navigator.clipboard.writeText(created.invite_link); toast.success("Copied"); }} className="rounded-lg p-1.5 hover:bg-white/10"><Copy size={16} /></button>
                </div>
              </div>
            )}
            <button onClick={() => setCreated(null)} className="mt-2 w-full rounded-full border border-border py-2.5 text-sm font-semibold">Done</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
